import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, ProgressBar, Tabs, Tab, Table } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FaGraduationCap, FaBook, FaVideo, FaClock, FaCheckCircle, FaRegClock, FaCalendarAlt, FaExclamationTriangle } from 'react-icons/fa';
import { getStudentEnrolledCourses, getStudentDashboardStats } from '../../services/courseService';
import { getCourseVideoProgress } from '../../services/videoService';
import { StudentEnrollment, VideoProgress, DashboardStats } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const StudentDashboard: React.FC = () => {
  const [enrolledCourses, setEnrolledCourses] = useState<StudentEnrollment[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get user data from the auth context
        if (!currentUser) {
          navigate('/login');
          return;
        }
        
        const userId = currentUser.uid;
        
        // Fetch enrolled courses
        const courses = await getStudentEnrolledCourses(userId);
        console.log("Enrolled courses:", courses); // للتأكد من البيانات
        setEnrolledCourses(courses);
        
        // Fetch dashboard stats
        const dashboardStats = await getStudentDashboardStats(userId);
        console.log("Dashboard stats:", dashboardStats); // للتأكد من البيانات
        setStats(dashboardStats);
        
        setLoading(false);
      } catch (err) {
        setError('حدث خطأ أثناء تحميل البيانات. يرجى المحاولة مرة أخرى.');
        setLoading(false);
        console.error(err);
      }
    };
    
    fetchData();
  }, [navigate, currentUser]);
  
  // Format seconds to hours:minutes:seconds
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  // Render loading state
  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">جاري تحميل البيانات...</p>
      </Container>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => window.location.reload()}>
          إعادة المحاولة
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-lg-5">
      <h1 className="mb-4">لوحة تحكم الطالب</h1>
      
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || 'overview')}
        className="mb-4"
      >
        <Tab eventKey="overview" title="نظرة عامة">
          {/* Stats Cards */}
          <Row className="mb-4">
            <Col lg={3} md={6} className="mb-3">
              <Card className="h-100 shadow-sm">
                <Card.Body className="d-flex align-items-center">
                  <div className="rounded-circle bg-primary p-3 text-white me-3">
                    <FaGraduationCap size={24} />
                  </div>
                  <div>
                    <h6 className="mb-0">الدورات المسجلة</h6>
                    <h3 className="mb-0">{stats?.enrolledCourses || 0}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <Card className="h-100 shadow-sm">
                <Card.Body className="d-flex align-items-center">
                  <div className="rounded-circle bg-success p-3 text-white me-3">
                    <FaCheckCircle size={24} />
                  </div>
                  <div>
                    <h6 className="mb-0">الدورات المكتملة</h6>
                    <h3 className="mb-0">{stats?.completedCourses || 0}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <Card className="h-100 shadow-sm">
                <Card.Body className="d-flex align-items-center">
                  <div className="rounded-circle bg-info p-3 text-white me-3">
                    <FaVideo size={24} />
                  </div>
                  <div>
                    <h6 className="mb-0">الدروس المكتملة</h6>
                    <h3 className="mb-0">{stats?.completedLessons || 0} / {stats?.totalLessons || 0}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <Card className="h-100 shadow-sm">
                <Card.Body className="d-flex align-items-center">
                  <div className="rounded-circle bg-warning p-3 text-white me-3">
                    <FaClock size={24} />
                  </div>
                  <div>
                    <h6 className="mb-0">وقت المشاهدة</h6>
                    <h3 className="mb-0">{formatTime(stats?.totalWatchTime || 0)}</h3>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Recent Courses */}
          <h4 className="mb-3">الدورات الحالية</h4>
          <Row>
            {enrolledCourses.length === 0 ? (
              <Col>
                <Alert variant="info">
                  لم تسجل في أي دورة بعد. <Link to="/courses">استكشف الدورات المتاحة</Link>
                </Alert>
              </Col>
            ) : (
              enrolledCourses.slice(0, 4).map((enrollment) => (
                <Col key={enrollment.id} lg={3} md={6} className="mb-4">
                  <Card className="h-100 shadow-sm course-card">
                    <div style={{ height: '160px', overflow: 'hidden', position: 'relative' }}>
                      {enrollment.courseImage ? (
                        <Card.Img 
                          variant="top" 
                          src={enrollment.courseImage} 
                          alt={enrollment.courseName || 'اسم الكورس غير متوفر'}
                          style={{ height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            target.src = 'https://via.placeholder.com/300x200?text=Course+Image';
                          }}
                        />
                      ) : (
                        <Card.Img 
                          variant="top" 
                          src="https://via.placeholder.com/300x200?text=Course+Image" 
                          alt={enrollment.courseName || 'اسم الكورس غير متوفر'}
                          style={{ height: '100%', objectFit: 'cover' }}
                        />
                      )}
                      <div 
                        className="position-absolute bottom-0 start-0 end-0 p-2" 
                        style={{ 
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                          color: 'white'
                        }}
                      >
                        <small className="d-flex align-items-center">
                          <FaCalendarAlt className="me-1" /> 
                          {new Date(enrollment.enrollmentDate).toLocaleDateString('ar-EG')}
                        </small>
                      </div>
                    </div>
                    <Card.Body className="d-flex flex-column">
                      <Card.Title className="mb-3 text-truncate fw-bold">
                        {enrollment.courseName || 'اسم الكورس غير متوفر'}
                      </Card.Title>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="text-muted">التقدم</small>
                          <small className="text-muted">{enrollment.progress}%</small>
                        </div>
                        <ProgressBar 
                          now={enrollment.progress} 
                          variant={enrollment.progress === 100 ? 'success' : 'info'}
                          style={{ height: '8px' }}
                        />
                      </div>
                      <div className="mt-auto">
                        <Button 
                          variant="info" 
                          onClick={() => navigate(`/courses/${enrollment.courseId}`)}
                          className="w-100 text-white"
                        >
                          متابعة التعلم
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
          
          {enrolledCourses.length > 4 && (
            <div className="text-center mt-3 mb-4">
              <Button variant="outline-primary" onClick={() => setActiveTab('courses')}>
                عرض جميع الدورات
              </Button>
            </div>
          )}
          
          {/* Upcoming Tasks */}
          <Row className="mt-4">
            <Col md={6} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Header className="bg-white">
                  <h5 className="mb-0">الواجبات القادمة</h5>
                </Card.Header>
                <Card.Body>
                  {stats?.upcomingAssignments ? (
                    <Alert variant="warning">
                      <FaExclamationTriangle className="me-2" />
                      لديك {stats.upcomingAssignments} واجب قيد الانتظار
                    </Alert>
                  ) : (
                    <Alert variant="success">
                      <FaCheckCircle className="me-2" />
                      ليس لديك واجبات قيد الانتظار
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Header className="bg-white">
                  <h5 className="mb-0">الاختبارات القادمة</h5>
                </Card.Header>
                <Card.Body>
                  {stats?.upcomingExams ? (
                    <Alert variant="warning">
                      <FaExclamationTriangle className="me-2" />
                      لديك {stats.upcomingExams} اختبار قيد الانتظار
                    </Alert>
                  ) : (
                    <Alert variant="success">
                      <FaCheckCircle className="me-2" />
                      ليس لديك اختبارات قيد الانتظار
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>
        
        <Tab eventKey="courses" title="الدورات المسجلة">
          <h4 className="mb-3">جميع الدورات المسجلة</h4>
          {enrolledCourses.length === 0 ? (
            <Alert variant="info">
              لم تسجل في أي دورة بعد. <Link to="/courses">استكشف الدورات المتاحة</Link>
            </Alert>
          ) : (
            <Row>
              {enrolledCourses.map((enrollment) => (
                <Col key={enrollment.id} lg={3} md={6} className="mb-4">
                  <Card className="h-100 shadow-sm course-card">
                    <div style={{ height: '160px', overflow: 'hidden', position: 'relative' }}>
                      {enrollment.courseImage ? (
                        <Card.Img 
                          variant="top" 
                          src={enrollment.courseImage} 
                          alt={enrollment.courseName || 'اسم الكورس غير متوفر'}
                          style={{ height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            target.src = 'https://via.placeholder.com/300x200?text=Course+Image';
                          }}
                        />
                      ) : (
                        <Card.Img 
                          variant="top" 
                          src="https://via.placeholder.com/300x200?text=Course+Image" 
                          alt={enrollment.courseName || 'اسم الكورس غير متوفر'}
                          style={{ height: '100%', objectFit: 'cover' }}
                        />
                      )}
                      <div 
                        className="position-absolute bottom-0 start-0 end-0 p-2" 
                        style={{ 
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                          color: 'white'
                        }}
                      >
                        <small className="d-flex align-items-center">
                          <FaCalendarAlt className="me-1" /> 
                          {new Date(enrollment.enrollmentDate).toLocaleDateString('ar-EG')}
                        </small>
                      </div>
                    </div>
                    <Card.Body className="d-flex flex-column">
                      <Card.Title className="mb-3 text-truncate fw-bold">
                        {enrollment.courseName || 'اسم الكورس غير متوفر'}
                      </Card.Title>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="text-muted">التقدم</small>
                          <small className="text-muted">{enrollment.progress}%</small>
                        </div>
                        <ProgressBar 
                          now={enrollment.progress} 
                          variant={enrollment.progress === 100 ? 'success' : 'info'}
                          style={{ height: '8px' }}
                        />
                      </div>
                      {enrollment.isPermanent && (
                        <Badge bg="success" className="mb-2">وصول دائم</Badge>
                      )}
                      <div className="mt-auto">
                        <Button 
                          variant="info" 
                          onClick={() => navigate(`/courses/${enrollment.courseId}`)}
                          className="w-100 text-white"
                        >
                          متابعة التعلم
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Tab>
        
        <Tab eventKey="assignments" title="الواجبات">
          <h4 className="mb-3">الواجبات</h4>
          <Alert variant="info">
            سيتم إضافة هذه الميزة قريبًا
          </Alert>
        </Tab>
        
        <Tab eventKey="exams" title="الاختبارات">
          <h4 className="mb-3">الاختبارات</h4>
          <Alert variant="info">
            سيتم إضافة هذه الميزة قريبًا
          </Alert>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default StudentDashboard;
